import { useEffect, useMemo } from "react"
import { decamelize } from "library/decamelize.mjs"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { Bound } from "lib/@components/binding/Bound"
import { emptyArray } from "lib/empty-array"
import { ExtendedSelect } from "lib/@components/extended-select/extended-select"
import { Grid2 as Grid } from "@mui/material"
import { FieldOption } from "./field-option"
import { FIXED_VALUE_OPTION } from "./plugins/fixed-value-option/fixed-value-option"
import { ExtraOptionsClear, ExtraOptionsUpdate } from "event-definitions"
import { If } from "lib/@components/switch"
import { AdditionalFieldInput } from "routes/asset-register/plugins/excel-import/additional-field-input"

export function FieldSelector({ item }) {
    const { label, field, required, multiple, unique, allowAdditional } = item
    const fieldLabel = `${label || `${decamelize(field).titleize()}`}`

    const [headers] = useBoundValue("headers", [])
    const [uniqueValues] = useBoundValue("uniqueValues")
    const [interpretedSheet] = useBoundValue("interpretedSheet")
    const [mapping, setMapping] = useBoundValue("fields." + field, multiple ? emptyArray : undefined)
    const [fixedMapping, setFixedMapping] = useBoundValue("fields." + field + "_fixed", undefined)

    const mappingValue = useMemo(() => {
        if (fixedMapping) return fixedMapping
        if (!multiple) return mapping?.name || ""
        return mapping?.map("name")
    }, [mapping, fixedMapping, multiple])

    const options = useMemo(
        () =>
            headers.filter(
                (header) =>
                    !uniqueValues ||
                    !interpretedSheet ||
                    !(unique && Object.keys(uniqueValues[header.name]).length < interpretedSheet.length)
            ),
        [headers, uniqueValues, interpretedSheet, unique]
    )

    useEffect(() => {
        if (fixedMapping) {
            ExtraOptionsUpdate(field).raise({ id: FIXED_VALUE_OPTION, value: fixedMapping })
        } else {
            ExtraOptionsClear(field).raise()
        }
    }, [field, fixedMapping])

    return (
        <Bound item={item} noOptions={options.length === 0} mapping={mapping} setMapping={setMapping}>
            <Grid size={{ xs: 6, md: 4 }}>
                <ExtendedSelect
                    selectId={field}
                    options={options}
                    label={fieldLabel}
                    multiple={multiple}
                    value={mappingValue}
                    required={required}
                    search={true}
                    onSelect={handleSelect}
                    onClear={handleClear}
                    OptionItem={FieldOption}
                />
                <If value={allowAdditional && mapping}>
                    <AdditionalFieldInput.Slot
                        type={field}
                        field={item}
                        mapping={mapping}
                        headers={headers}
                        uniqueValues={uniqueValues}
                        sheet={interpretedSheet}
                    />
                </If>
            </Grid>
        </Bound>
    )

    function handleSelect(value) {
        if (multiple) {
            const selectedOptions = headers.filter((header) => value.includes(header.name))
            setMapping(selectedOptions)
        } else {
            if (!value || value === FIXED_VALUE_OPTION) {
                setMapping(undefined)
            } else {
                setFixedMapping(undefined)
                const selectedOption = headers.find((header) => header.name === value)
                setMapping(selectedOption)
            }
        }
    }

    function handleClear() {
        multiple ? setMapping([]) : setMapping(undefined)
        setFixedMapping(undefined)
    }
}
